import {Component, ElementRef, HostListener, OnDestroy, OnInit, ViewChild} from '@angular/core';
import {TranslocoService} from '@ngneat/transloco';
import * as moment from 'moment';
import {Subscription} from 'rxjs';
import {AuthGuard} from 'src/app/common/auth-guard.service';
import {Machine} from 'src/app/model/machine';
import {MachineLastValuesCharts} from 'src/app/model/custom/machine/machine-last-values-charts';
import {MachineDetailsService} from 'src/app/services/machine-details.service';
import {MachinesService} from 'src/app/services/machines.service';
import {Period} from '../machine-details-data/machine-details-data.component';
import {TimezoneService} from "../../../../services/timezone.service";
import {CommonTranslationsService} from "../../../../services/common-translations.service";
import {ExportFileType} from "../../../../model/export-file-type";

@Component({
  selector: 'app-machine-details-charts',
  templateUrl: './machine-details-charts.component.html',
  styleUrls: ['./machine-details-charts.component.scss']
})
export class MachineDetailsChartsComponent implements OnInit, OnDestroy {

  @ViewChild('squeezedValuesByHourOfDayDiv', {static: true}) squeezedValuesByHourOfDayDiv: ElementRef;
  @ViewChild('squeezedValuesByDayOfWeekDiv', {static: true}) squeezedValuesByDayOfWeekDiv: ElementRef;
  @ViewChild('squeezedValuesByMonthOfYearDiv', {static: true}) squeezedValuesByMonthOfYearDiv: ElementRef;

  public machine: Machine = null;
  public isMachineLoading: boolean = true;
  public areChartsLoading: boolean = true;
  public isPeriodChartInfoLoading: boolean = true;

  public tempSelectedPeriod: Period[] = ['lastDay'];
  public selectedPeriod: Period[] = ['lastDay'];
  public isSpecificPeriodSelected: boolean = false;
  public tempSpecificPeriodRange: [any, any] = null;
  public specificPeriodRange: [any, any] = [null, null];
  public isPeriodRangeValid: boolean = false;
  public filtersVisible: boolean = false;
  public filtersViewInLitersTemp: boolean = false;
  public filtersViewInLiters: boolean = false;
  public availableYearRange: string;

  public operatingTime: string;
  public outOfServiceTime: string;

  public squeezedValuesMeasurements: any;
  public squeezedValuesByHourOfDay: { categories: string[], data: number[] };
  public squeezedValuesByDayOfWeek: { categories: string[], data: number[] };
  public squeezedValuesByMonthOfYear: { categories: string[], data: number[] };

  public plotOptions: any;
  public squeezedValuesByHourOfDayDivWidth: number;
  public squeezedValuesByDayOfWeekDivWidth: number;
  public squeezedValuesByMonthOfYearDivWidth: number;

  public squeezedValuesTranslation: string;
  public squeezedValuesTranslationPeriodInfo: string;
  public squeezedValuesByHourOfLastDayTranslation: string;
  public squeezedValuesByDayOfLastWeekTranslation: string;
  public squeezedValuesByMonthOfLastYearTranslation: string;
  public totalSqueezedValuesInPeriodTranslation: string;

  public updateFlag: boolean = false;

  public exportVisible: boolean;
  public viewInLiters: boolean = false;
  public exportFileType: ExportFileType;

  private periodRange: [number, number] = [null, null];
  private machineDetailsSubscription: Subscription;
  private machineLastValuesCharts: MachineLastValuesCharts;

  private hoursOfDay: string[] = [];
  private daysOfWeek: string[] = [];
  private months: string[] = [];

  private daysTranslation: string;
  private hoursTranslation: string;
  private minutesTranslation: string;
  private counterValueTranslation: string;
  private startTranslation: string;
  private endTranslation: string;
  private totalOperatingHoursTranslation: string;
  private machineOperatingTimeTranslation: string;


  constructor(
    private readonly machinesService: MachinesService,
    private readonly machineDetailsService: MachineDetailsService,
    private readonly i18n: TranslocoService,
    private readonly timezoneService: TimezoneService,
    private readonly commonTranslationsService: CommonTranslationsService,
    public authGuard: AuthGuard
  ) {
    this.hoursOfDay = this.timezoneService.getHoursOfDay();
  }

  ngOnInit(): void {
    this.periodRange = this.getPeriodRange(this.selectedPeriod[0], this.isPeriodRangeValid);
    this.availableYearRange = this.timezoneService.calcYearRange();
    this.loadTranslations();
    this.setPlotOptions();

    this.machineDetailsSubscription = this.machineDetailsService.$notifier.subscribe(machineDetails => {
      this.machine = machineDetails.machine;

      if (machineDetails.machineLoading === false) {
        this.machine = machineDetails.machine;
        this.getLastValuesCharts();
        this.getMachinePeriodChartInfo();
      }
    });
  }

  private loadTranslations(): void {
    this.i18n.selectTranslation().subscribe(() => {
      this.daysTranslation = (this.i18n.translate('time.days:short'));
      this.hoursTranslation = this.i18n.translate('time.hours:short');
      this.minutesTranslation = this.i18n.translate('time.minutes:short');
      this.counterValueTranslation = this.i18n.translate('misc.overview.counterValue');
      this.startTranslation = this.i18n.translate('misc.overview.start');
      this.endTranslation = this.i18n.translate('misc.overview.end');
      this.totalOperatingHoursTranslation = this.i18n.translate('misc.overview.totalOperatingHours');
      this.machineOperatingTimeTranslation = this.i18n.translate('misc.info.charts.machineOperatingTime');
      this.hoursOfDay = this.hoursOfDay.map(h => h + this.hoursTranslation);
      this.loadLastValuesTranslations();
      this.loadPeriodChartsInfoTranslations();
      this.loadChartsTranslations();
    });
  }

  private loadLastValuesTranslations(): void {
    this.squeezedValuesTranslation = this.viewInLiters ?
      this.i18n.translate('misc.overview.squeezedLiters') :
      this.i18n.translate('misc.overview.squeezedFruits');
    this.squeezedValuesByHourOfLastDayTranslation = this.viewInLiters ?
      this.i18n.translate('misc.info.charts.squeezedLitersByHourOfLastDay') :
      this.i18n.translate('misc.info.charts.squeezedFruitsByHourOfLastDay');
    this.squeezedValuesByDayOfLastWeekTranslation = this.viewInLiters ?
      this.i18n.translate('misc.info.charts.squeezedLitersByDayOfLastWeek') :
      this.i18n.translate('misc.info.charts.squeezedFruitsByDayOfLastWeek');
    this.squeezedValuesByMonthOfLastYearTranslation = this.viewInLiters ?
      this.i18n.translate('misc.info.charts.squeezedLitersByMonthOfLastYear') :
      this.i18n.translate('misc.info.charts.squeezedFruitsByMonthOfLastYear');
  }

  private loadPeriodChartsInfoTranslations(): void {
    this.squeezedValuesTranslationPeriodInfo = this.filtersViewInLiters ? this.i18n.translate('misc.overview.squeezedLiters') : this.i18n.translate('misc.overview.squeezedFruits');
    this.totalSqueezedValuesInPeriodTranslation = this.filtersViewInLiters ? this.i18n.translate('misc.info.charts.totalSqueezedLitersInPeriod') : this.i18n.translate('misc.info.charts.totalSqueezedFruitsInPeriod');
  }

  private loadChartsTranslations(): void {
    this.commonTranslationsService.getDaysOfWeek().subscribe((daysOfWeek: string[]) => {
      this.daysOfWeek = daysOfWeek;
    });
    this.commonTranslationsService.getMonthsOfYear().subscribe((monthsOfYear: string[]) => {
      this.months = monthsOfYear;
    });
  }

  ngAfterViewInit(): void {
    this.squeezedValuesByHourOfDayDivWidth = this.squeezedValuesByHourOfDayDiv.nativeElement.offsetWidth;
    this.squeezedValuesByDayOfWeekDivWidth = this.squeezedValuesByDayOfWeekDiv.nativeElement.offsetWidth;
    this.squeezedValuesByMonthOfYearDivWidth = this.squeezedValuesByMonthOfYearDiv.nativeElement.offsetWidth;
  }

  @HostListener('window:resize', ['$event'])
  onWindowResize() {
    this.squeezedValuesByHourOfDayDivWidth = this.squeezedValuesByHourOfDayDiv.nativeElement.offsetWidth;
    this.squeezedValuesByDayOfWeekDivWidth = this.squeezedValuesByDayOfWeekDiv.nativeElement.offsetWidth;
    this.squeezedValuesByMonthOfYearDivWidth = this.squeezedValuesByMonthOfYearDiv.nativeElement.offsetWidth;
    this.updateFlag = true;
  }

  private setPlotOptions(): void {
    this.plotOptions = {
      series: {
        color: getComputedStyle(document.documentElement).getPropertyValue('--primary-color')
      }
    }
  }

  private getLastValuesCharts(): void {
    this.machinesService.getMachineLastValuesChartsByUuid(this.machine.machineUuid, this.viewInLiters)
      .subscribe(response => {
        if (response.machineLastValuesCharts != null) {
          this.machineLastValuesCharts = response.machineLastValuesCharts;

          this.i18n.selectTranslation().subscribe(() => {
            this.squeezedValuesByHourOfDay = this.parseMap(
              this.machineLastValuesCharts.squeezedValuesByHourOfDay,
              this.hoursOfDay,
              0
            );
            this.squeezedValuesByDayOfWeek = this.parseMap(
              this.machineLastValuesCharts.squeezedValuesByDayOfWeek,
              this.daysOfWeek, 0, 1
            );
            this.squeezedValuesByMonthOfYear = this.parseMap(
              this.machineLastValuesCharts.squeezedValuesByMonthOfYear,
              this.months, 0, 1
            );

            this.areChartsLoading = false;
          })
        }

        this.isMachineLoading = false;
      });
  }

  private getMachinePeriodChartInfo(): void {
    this.machinesService.getMachinePeriodChartInfoByUuid(this.machine.machineUuid, this.periodRange[0], this.periodRange[1], this.filtersViewInLiters)
      .subscribe(response => {
        if (response.machineChartsInfo.squeezedValuesMeasurements != null) {
          this.squeezedValuesMeasurements = response.machineChartsInfo.squeezedValuesMeasurements.map(measure => {
            return [moment(measure.timestamp).utc().valueOf(), parseFloat(measure.value)]
          });
        }
        this.operatingTime = response.machineChartsInfo.operatingMinutes != null ? this.minutesToTime(response.machineChartsInfo.operatingMinutes) : null;
        this.outOfServiceTime = response.machineChartsInfo.outOfServiceMinutes != null ? this.minutesToTime(response.machineChartsInfo.outOfServiceMinutes) : null;
        this.isPeriodChartInfoLoading = false;
      });
  }

  changeLiters(event: any): void {
    this.viewInLiters = event.checked;
    this.areChartsLoading = true;
    this.i18n.selectTranslation().subscribe(() => {
      this.loadLastValuesTranslations();
      this.getLastValuesCharts();
    });
  }

  public tooltipFormatter(unit: string): string {
    return this.getTooltipFormatter(unit, this.viewInLiters);
  }

  public tooltipFormatterPeriodInfo(unit: string): string {
    return this.getTooltipFormatter(unit, this.filtersViewInLiters);
  }

  private getTooltipFormatter(unit: string, isInLiters: boolean): string {
    const numberOfDecimals: number = isInLiters ? 3 : 0;
    return `{point.y:,.${numberOfDecimals}f} ${unit}`;
  }

  public selectPeriod(period: Period): void {
    this.tempSelectedPeriod = [period];
  }

  public applySelection(): void {
    this.isPeriodChartInfoLoading = true;
    this.filtersViewInLiters = this.filtersViewInLitersTemp;
    this.selectedPeriod = this.tempSelectedPeriod;
    this.isSpecificPeriodSelected = this.tempSelectedPeriod[0] === 'specificPeriod';
    if (!this.isSpecificPeriodSelected) {
      this.periodRange = this.getPeriodRange(this.selectedPeriod[0], this.isPeriodRangeValid);
    } else {
      this.specificPeriodRange[0] = this.tempSpecificPeriodRange[0];
      this.specificPeriodRange[1] = this.tempSpecificPeriodRange[1];
      this.periodRange = this.getPeriodRange(this.selectedPeriod[0], this.isPeriodRangeValid, this.specificPeriodRange);
    }
    this.i18n.selectTranslation().subscribe(() => {
      this.loadPeriodChartsInfoTranslations();
      this.getMachinePeriodChartInfo();
    });
  }

  public resetSelection(): void {
    this.tempSelectedPeriod = ['lastDay'];
  }

  private getPeriodRange(period: Period, periodRangeValid: boolean, periodRange?: [any, any]): [number, number] {
    return this.timezoneService.getPeriodRange(period, periodRangeValid, periodRange);
  }

  private sliceArrayToLeft<T>(array: T[], n: number): T[] {
    const position: number = n % array.length;
    return array.slice(position).concat(array.slice(0, position));
  }

  private getData(map: Map<string, number>, length: number, initIndex: number): number[] {
    const mapIterator = map.entries();
    const size: number = map.size;

    if (size > 0) {
      const array: number[] = Array.from({length}, (_, i) => 0);

      for (let i = 0; i < size; i++) {
        const value = mapIterator.next().value;
        array[value[0] - initIndex] = value[1];
      }
      return array;
    }
    return null;
  }

  private parseMap(object: any, categories: string[], slicePositions: number = 0, initIndex: number = 0): {
    categories: string[],
    data: number[]
  } {
    if (object != null) {
      const map: Map<string, number> = new Map<string, number>(Object.entries(object));
      const size: number = map.size;

      if (size > 0) {
        return {
          categories: this.sliceArrayToLeft<string>(categories, slicePositions),
          data: this.sliceArrayToLeft<number>(this.getData(map, categories.length, initIndex), slicePositions)
        }
      }
    }

    return {
      categories: categories,
      data: null
    };
  }

  public validatePeriodRange(specificPeriodRange: [any, any]): void {
    this.tempSpecificPeriodRange = specificPeriodRange;
    this.isPeriodRangeValid = specificPeriodRange?.[0] != null && specificPeriodRange?.[1] != null;
  }

  public isFilterButtonDisabled(): boolean {
    return this.tempSelectedPeriod[0] === 'specificPeriod' && !this.isPeriodRangeValid;
  }

  public minutesToTime(minutes: number): string {
    if (minutes != 0) {
      let hours: number = minutes / 60;
      let days: number = hours / 24;

      return this.timeFormatter(Math.floor(days), Math.floor(hours) % 24, Math.floor(minutes) % 60)
    }
    return '0 ' + this.minutesTranslation;
  }

  private timeFormatter(days: number, hours: number, minutes: number): string {
    let duration: string = this.timezoneService.timeFormatterDaysHoursAndMinutes(days, hours, minutes, this.daysTranslation,
      this.hoursTranslation, this.minutesTranslation);
    if (duration === '') {
      duration = minutes + ' ' + this.minutesTranslation;
    }
    return duration;
  }

  public getContentExport(): any[] {
    if (this.exportFileType === ExportFileType.MACHINE_CHARTS) {
      return [this.squeezedValuesByHourOfDay, this.squeezedValuesByDayOfWeek, this.squeezedValuesByMonthOfYear, this.viewInLiters];
    } else {
      return [this.operatingTime, this.outOfServiceTime, this.squeezedValuesMeasurements, this.periodRange, this.filtersViewInLiters];
    }
  }

  ngOnDestroy(): void {
    if (this.machineDetailsSubscription != null) this.machineDetailsSubscription.unsubscribe();
  }

  public readonly ExportFileType = ExportFileType;
}
